// =============================================================================
// features/sales/agenda/components/NewAppointmentFlow.tsx — "Novo
// agendamento" da Agenda comercial (F35-S24, doc 26 §6.1).
//
// A Agenda começa do zero — ao contrário do live chat (`ScheduleReturnButton`)
// e do CRM (`CrmDetailPage`), que já estão "dentro" de um lead — então o
// `ScheduleAppointmentDialog` (features/sales/components/, REUTILIZADO como
// está, fora de escopo tocar) só abre depois que o vendedor escolhe o lead
// em `LeadPickerDialog`.
//
// Máquina de 2 passos, nunca simultâneos:
//   'picking'    → LeadPickerDialog (busca com escopo/RBAC 100% no servidor).
//   'scheduling' → ScheduleAppointmentDialog com o leadId escolhido.
// `afterLeadSelected` é pura e exportada para teste (sem RTL neste pacote —
// mesmo padrão de `AppointmentDrawer.shouldBlockEscape`).
//
// Depois de criar, a lista da Agenda atualiza sozinha: `useCreateAppointment`
// (agenda/hooks.ts) já invalida `appointmentKeys.all` — nenhuma invalidação
// nova precisa viver aqui.
// =============================================================================

import * as React from 'react';

import { useToast } from '../../../../components/ui/Toast';
import type { LeadResponse } from '../../../../hooks/sales/useLeadSearch';
import { ScheduleAppointmentDialog } from '../../components/ScheduleAppointmentDialog';

import { LeadPickerDialog } from './LeadPickerDialog';

export type NewAppointmentStep = 'closed' | 'picking' | 'scheduling';

export interface NewAppointmentState {
  step: NewAppointmentStep;
  lead: LeadResponse | null;
}

export const CLOSED_STATE: NewAppointmentState = { step: 'closed', lead: null };
export const PICKING_STATE: NewAppointmentState = { step: 'picking', lead: null };

/**
 * Selecionar um lead no picker sempre avança para o agendamento — nunca fica
 * "preso" entre os dois passos.
 */
export function afterLeadSelected(lead: LeadResponse): NewAppointmentState {
  return { step: 'scheduling', lead };
}

export interface NewAppointmentFlowProps {
  /** Controlado pelo cabeçalho (botão "Novo agendamento"). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewAppointmentFlow({
  open,
  onOpenChange,
}: NewAppointmentFlowProps): React.JSX.Element | null {
  const { toast } = useToast();
  const [state, setState] = React.useState<NewAppointmentState>(CLOSED_STATE);

  React.useEffect(() => {
    setState(open ? PICKING_STATE : CLOSED_STATE);
  }, [open]);

  function close(): void {
    setState(CLOSED_STATE);
    onOpenChange(false);
  }

  if (state.step === 'picking') {
    return (
      <LeadPickerDialog onClose={close} onSelect={(lead) => setState(afterLeadSelected(lead))} />
    );
  }

  if (state.step === 'scheduling' && state.lead) {
    return (
      <ScheduleAppointmentDialog
        leadId={state.lead.id}
        leadName={state.lead.name}
        onClose={close}
        onScheduled={() => {
          toast('Agendamento criado com sucesso.', 'success');
          close();
        }}
      />
    );
  }

  return null;
}
